import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { useScrollLock } from "@eave/shared/hooks";
import { AppShape, DocShape } from "@eave/shared/types/api";
import APIClient from "web_modules/APIClient";
import Spinner from "web_modules/shared/Spinner";
import { docTypeToHumanStr } from "../shared";

const styles = require("./styles.module.scss");

interface MatchParams {
  appId: string;
  docId: string;
}

export const formatDateTime = (date: string): string => {
  const d = new Date(date);
  const locale = navigator.language || "en-US";
  const dateOptions = {
    month: "short",
    day: "numeric",
  };
  const timeOptions = {
    hour: "numeric",
    minute: "numeric",
    timeZoneName: "short",
  };

  return `${d.toLocaleDateString(locale, dateOptions)} | ${d.toLocaleTimeString(
    locale,
    timeOptions,
  )}`;
};

export const ErrorMessage = ({ type, id }): React.ReactElement => (
  <div className={styles.sorry}>
    <h2>Sorry, we couldn&apos;t find that {type}.</h2>
    <p>
      Is <strong>#{id}</strong> the correct {type} ID?
    </p>
  </div>
);

const DocumentViewer: React.FunctionComponent<RouteComponentProps<MatchParams>> = (
  props,
): React.ReactElement => {
  const [loading, setLoading] = React.useState(true);
  const [doc, setDoc] = React.useState<any>(null);
  const [app, setApp] = React.useState<AppShape>(null);

  const {
    match: {
      params: { appId, docId },
    },
  } = props;

  // Prevent double scroll bars
  useScrollLock();

  React.useEffect(() => {
    setLoading(true);
    APIClient.get(`/loan_applications/${appId}`)
      .then(({ application }) => {
        setApp(application);

        // Different treatment for docs linked to an application
        // versus S3 documents created from "View selected docs" PDF merge
        if (Number.isInteger(+docId)) {
          setDoc(application.all_documents.find(document => document.id === +docId));
        } else {
          setDoc({
            s3_key: `merged_documents/${application.computed_properties.lead_number}/${docId}.pdf`,
          });
        }
      })
      .catch(error => {
        throw new Error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [appId, docId]);

  if (loading) {
    return <Spinner active={loading} />;
  }

  if (!app) {
    return <ErrorMessage type="application" id={appId} />;
  }

  if (!doc) {
    return <ErrorMessage type="document" id={docId} />;
  }

  return (
    <div className={styles.container}>
      <header>
        <ul>
          <li>Loan #: {app.computed_properties.loan_number || "---"}</li>
          <li>Lead #: {app.computed_properties.lead_number}</li>
          <li>
            B1: {app.borrower1_first_name} {app.borrower1_last_name}
          </li>
          {app.borrower2_first_name && (
            <li>
              B2: {app.borrower2_first_name} {app.borrower2_last_name}
            </li>
          )}
          <li>
            Property:{" "}
            {(app.computed_properties.purchased_property_address &&
              app.computed_properties.purchased_property_address.line1) ||
              "---"}
          </li>
          <li>{doc.id}</li>
          <li>{docTypeToHumanStr(doc.type)}</li>
          <li>{doc.filename}</li>
          <li>{doc.created_at && formatDateTime(doc.created_at)}</li>
        </ul>
      </header>
      <iframe title="Document viewer" src={`/files/${doc.s3_key}`} />
    </div>
  );
};

export default DocumentViewer;
